import React, { useState, useEffect } from "react";
import { BrandLogo } from "../brand/BrandLogo";
import { Link, useNavigate } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { NAV_LINKS } from "../../mock/mockData";
import { useAuth } from "../../context/AuthContext";

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (href) => {
    setOpen(false);
    const id = href.split("#")[1];
    if (!id) return;
    const el = document.getElementById(id);
    if (el) { el.scrollIntoView({ behavior: "smooth" }); }
    else { navigate("/"); setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }), 400); }
  };

  const linkCls = "text-[15px] font-medium text-brand-green/80 hover:text-brand-greenDeep transition-colors";
  const TOOLS = [
    { to: "/simulateur-lmnp-2027", label: "Simulateur LMNP 2027", badge: "Nouveau" },
    { to: "/quittance-de-loyer/generateur", label: "Générateur de quittance" },
    { to: "/revision-loyer-irl/calculateur", label: "Calculateur IRL" },
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-white/90 backdrop-blur-md shadow-sm" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 lg:px-8 h-[72px] flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <BrandLogo variant="clair" className="h-8 sm:h-9 w-auto" />
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {NAV_LINKS.map((l) => (
            <button
              key={l.label}
              onClick={() => scrollTo(l.href)}
              className={linkCls}
            >
              {l.label}
            </button>
          ))}
          <div className="relative group" data-testid="nav-tools">
            <button className={`${linkCls} inline-flex items-center gap-1`} data-testid="nav-tools-btn">Outils <ChevronDown className="w-4 h-4 transition-transform group-hover:rotate-180" /></button>
            <div className="absolute left-1/2 -translate-x-1/2 top-full pt-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-opacity duration-200">
              <div className="w-64 rounded-2xl bg-white border border-brand-soft shadow-xl p-2">
                {TOOLS.map((t) => (
                  <Link key={t.to} to={t.to} className="flex items-center justify-between px-3 py-2.5 rounded-xl text-sm font-medium text-brand-greenDeep hover:bg-brand-softer transition-colors" data-testid={`nav-tool-${t.to.split("/").pop()}`}>
                    {t.label}{t.badge && <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-brand-lime text-brand-greenDeep">{t.badge}</span>}
                  </Link>
                ))}
              </div>
            </div>
          </div>
          <Link to="/blog" className={linkCls} data-testid="nav-blog">Blog</Link>
          <Link to="/devenir-referent" className={linkCls} data-testid="nav-devenir-referent">Devenir référent</Link>
        </nav>

        <div className="hidden md:flex items-center gap-5">
          {user ? (
            <button
              onClick={() => navigate("/app")}
              className="px-5 py-2.5 rounded-full bg-brand-lime hover:bg-brand-limeDark text-brand-greenDeep font-semibold text-[15px] transition-colors"
            >
              Mon espace
            </button>
          ) : (
            <>
              <button
                onClick={() => navigate("/connexion")}
                className="text-[15px] font-medium text-brand-green/80 hover:text-brand-greenDeep transition-colors"
              >
                Connexion
              </button>
              <button
                onClick={() => navigate("/connexion")}
                className="px-5 py-2.5 rounded-full bg-brand-lime hover:bg-brand-limeDark text-brand-greenDeep font-semibold text-[15px] transition-colors shadow-sm"
              >
                Inscription
              </button>
            </>
          )}
        </div>

        <button className="md:hidden text-brand-greenDeep" onClick={() => setOpen(!open)}>
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && (
        <div className="md:hidden bg-white border-t border-brand-soft px-5 py-4 space-y-3 shadow-lg">
          {NAV_LINKS.map((l) => (
            <button
              key={l.label}
              onClick={() => scrollTo(l.href)}
              className="block w-full text-left py-2 font-medium text-brand-green"
            >
              {l.label}
            </button>
          ))}
          <p className="pt-2 text-xs font-semibold uppercase tracking-wide text-brand-green/50">Outils</p>
          {TOOLS.map((t) => (
            <Link key={t.to} to={t.to} onClick={() => setOpen(false)} className="flex items-center gap-2 w-full text-left py-2 font-medium text-brand-green" data-testid={`nav-tool-mobile-${t.to.split("/").pop()}`}>
              {t.label}{t.badge && <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-brand-lime text-brand-greenDeep">{t.badge}</span>}
            </Link>
          ))}
          <Link to="/blog" onClick={() => setOpen(false)} className="block w-full text-left py-2 font-medium text-brand-green" data-testid="nav-blog-mobile">Blog</Link>
          <Link to="/devenir-referent" onClick={() => setOpen(false)} className="block w-full text-left py-2 font-medium text-brand-green" data-testid="nav-devenir-referent-mobile">Devenir référent</Link>
          <button
            onClick={() => navigate("/connexion")}
            className="block w-full py-2.5 rounded-full bg-brand-lime text-brand-greenDeep font-semibold text-center"
          >
            Inscription
          </button>
        </div>
      )}
    </header>
  );
}
